<template lang="pug">
	.app-core--waveform.flex-col.space(:style="{ '--height': `${height}px` }")
		.flex-row.justify.lr-padding
			.flex-row.space.align-center
				template(v-if="showControls")
					.audio-tracker-button.flex-row.center.align-center(@click="play(0)", :class="{ 'disabled-controls': isPlaying }", :title="$tCaption('Play', '@actions')")
						fas(icon="play")
					.audio-tracker-button.flex-row.center.align-center(@click="stop()", :class="{ 'disabled-controls': !isPlaying }", :title="$tCaption('Stop', '@actions')")
						fas(icon="stop")
				slot(name="actions-left")
			.flex-row.space.align-center
				slot(name="actions-right")
		.flex-col.full-width.canvas-area(ref="canvasArea", :style="{ '--position': `${sliderDotPosition}%` }", @click="onAudioPositionChange")
			canvas(ref="canvas")
			.slider-dot
</template>

<script>
	import axios from 'axios';
	import waveform from 'app-core/waveform';
	import VueTypes from 'vue-types';

	export default {	
		props: {
			audioBuffer: VueTypes.any.def(null), 
			src: VueTypes.string.def(null),
			context: VueTypes.any.def(null),
			showControls: VueTypes.bool.def(true),
			opts: VueTypes.any.def(null),
			height: VueTypes.number.def(100)
		},
		data() {
			return {
				blobBuffer: this.audioBuffer,
				currentTime: null,
				isPlaying: false,
				audioContext: this.context || new AudioContext(),
				startedTime: null
			}
		},
		computed: {
			sliderDotPosition() {
				return Math.min(1, this.currentTime / this.blobBuffer?.duration) * 100;
			},
		},
		watch: {
			src() {
				this.loadSrc();
			},
			blobBuffer() {
				this.drawCanvas();
			},
			opts: {
				handler() {
					this.drawCanvas();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.waveform = this;
			if (this.blobBuffer) {
				this.drawCanvas();
			} 
			if (this.src) {
				this.loadSrc();
			}
		},
		methods: {
			loadSrc() {
				return new Promise(resolve => {
					axios.get(this.src, {
						responseType: 'blob'
					}).then(r => {
						var fr = new FileReader();
						fr.readAsArrayBuffer(r.data);						
						fr.onloadend = () => {
							this.audioContext.decodeAudioData(fr.result, (buffer) => {
								this.blobBuffer = buffer;
								resolve(buffer);
							},() => resolve(null));
						};

						var fr1 = new FileReader();
						fr1.readAsDataURL(r.data);
						fr1.onloadend = () => {
							this.$emit('src-load', fr1.result);
						}
					}).catch(() => resolve(null));
				});
			},
			drawCanvas() {
				if (this.blobBuffer) {
					setImmediate(() => {
						if (this._isDestroyed) return;
						waveform.drawAudioData(this.blobBuffer, this.$refs.canvas, this.$refs.canvasArea, { color: '#aaa', heightMultiplier: 2, ...(this.opts || {}) });
					});
				}
			},
			onAudioPositionChange(e) {
				const time = e?.offsetX / this.$refs.canvasArea?.offsetWidth * this.blobBuffer?.duration || 0;
				if (__DEBUG__)
					console.debug('On slider change', { e, time, lx: e?.layerX, ow: this.$refs.canvasArea?.offsetWidth, bd: this.blobInfo?.blobBuffer?.duration  });
				if (this.isPlaying) {
					this.play(time);
				} else {
					this.currentTime = time;
				}
			},
			stop() {
				if (this.$source) {
					const src = this.$source;
					this.$source = null;
					src.stop();
					this.isPlaying = false;
				}

				if (this.$updateTimer)
					clearInterval(this.$updateTimer);
			},
			play(position) {
				if (__DEBUG__)
					console.debug('Play');
				this.stop();
				const buffer = this.blobBuffer;
				if (!position)
					position = this.currentTime || 0;
				if (position >= buffer?.duration - 0.01)
					position = 0;
				let source = this.audioContext.createBufferSource();
				source.buffer = buffer;
				source.connect(this.audioContext.destination);
				source.start(this.audioContext.currentTime, position || void 0);
				source.addEventListener('ended', () => {
					if (this.$source === source) {
						this.$source = null;
						this.isPlaying = false;
						this.stop();
					}
				});
				this.$updateTimer = setInterval(() => {
					this.currentTime = this.audioContext.currentTime - this.startedTime + (position || 0);
				});
				this.$source = source;
				this.startedTime = this.audioContext.currentTime;
				setImmediate(() => {
					this.isPlaying = true;
				});
			},
		}
	};
</script>
<style lang="scss">
	.app-core--waveform {
		.canvas-area {
			--bg: #e9e9e9;
			height: var(--height);
			position: relative;
			margin-left: -20px;
			width: calc(100% + 40px);
			border-top: 1px solid #ccc;
			border-bottom: 1px solid #ccc;
			cursor: pointer;

			> canvas {
				width: 100%;
				height: 100%;
				background: var(--bg);
			}

			.slider-dot {
				position: absolute;
				top: 50%;
				left: var(--position);
				width: 1px;
				height: 100%;
				transform: translateX(-50%) translateY(-50%);
				background: #ff0000;
				border-radius: 40px;
			}
		}
	}
</style>
